/**
 * Enhanced Sentry Client-Side Error Tracking
 * Provides utilities for capturing errors with better context for debugging
 */

import * as Sentry from "@sentry/nextjs";

/**
 * Set user context for better error tracking
 * Call this after user logs in
 */
export function setSentryUser(user: {
  id?: string;
  email?: string;
  username?: string;
  role?: string;
  companyId?: string;
}) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
    role: user.role,
    companyId: user.companyId,
  });
}

/**
 * Clear user context (call on logout)
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Set additional context for the current scope
 */
export function setSentryContext(key: string, context: Record<string, any>) {
  Sentry.setContext(key, context);
}

/**
 * Add breadcrumb for debugging
 */
export function addSentryBreadcrumb(
  message: string,
  category?: string,
  level: "debug" | "info" | "warning" | "error" | "fatal" = "info",
  data?: Record<string, any>
) {
  Sentry.addBreadcrumb({
    message,
    category: category || "custom",
    level,
    data,
    timestamp: Date.now() / 1000,
  });
}

/**
 * Capture error with enhanced context
 * Use this instead of console.error for better debugging
 */
export function captureError(
  error: Error | string,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, any>;
    level?: "error" | "warning" | "info" | "fatal";
    user?: {
      id?: string;
      email?: string;
      role?: string;
    };
  }
) {
  const errorObj = typeof error === "string" ? new Error(error) : error;

  Sentry.withScope((scope) => {
    // Add tags
    if (context?.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    // Add extra context
    if (context?.extra) {
      Object.entries(context.extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }

    // Set user if provided
    if (context?.user) {
      scope.setUser(context.user);
    }

    // Set level
    scope.setLevel(context?.level || "error");

    // Capture the error
    Sentry.captureException(errorObj);
  });
}

/**
 * Capture message (non-error events)
 */
export function captureMessage(
  message: string,
  level: "debug" | "info" | "warning" | "error" | "fatal" = "info",
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, any>;
  }
) {
  Sentry.withScope((scope) => {
    if (context?.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    if (context?.extra) {
      Object.entries(context.extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }

    scope.setLevel(level);
    Sentry.captureMessage(message, level);
  });
}

/**
 * Track page navigation
 */
export function trackPageView(pageName: string, additionalData?: Record<string, any>) {
  addSentryBreadcrumb(`Navigated to ${pageName}`, "navigation", "info", additionalData);
  setSentryContext("page", {
    name: pageName,
    url: typeof window !== "undefined" ? window.location.href : undefined,
    ...additionalData,
  });
}

/**
 * Track API call errors
 */
export function trackApiError(
  endpoint: string,
  error: Error | string,
  requestData?: {
    method?: string;
    status?: number;
    response?: any;
    body?: any;
  }
) {
  captureError(error, {
    tags: {
      error_type: "api_error",
      endpoint,
      method: requestData?.method || "unknown",
      status: requestData?.status?.toString() || "unknown",
    },
    extra: {
      endpoint,
      method: requestData?.method,
      status: requestData?.status,
      response: requestData?.response,
      requestBody: requestData?.body,
      url: typeof window !== "undefined" ? window.location.href : undefined,
    },
  });
}

/**
 * Track login errors specifically
 */
export function trackLoginError(
  error: Error | string,
  context?: {
    email?: string;
    hasMfa?: boolean;
    step?: "credentials" | "mfa" | "session";
    errorCode?: string;
  }
) {
  captureError(error, {
    tags: {
      error_type: "login_error",
      login_step: context?.step || "unknown",
      has_mfa: context?.hasMfa ? "true" : "false",
    },
    extra: {
      email: context?.email ? maskEmail(context.email) : undefined,
      hasMfa: context?.hasMfa,
      step: context?.step,
      errorCode: context?.errorCode,
      page: "login",
      url: typeof window !== "undefined" ? window.location.href : undefined,
    },
    level: "error",
  });
}

/**
 * Track dashboard errors
 */
export function trackDashboardError(
  error: Error | string,
  context?: {
    companyId?: string;
    page?: string;
    component?: string;
    action?: string;
  }
) {
  captureError(error, {
    tags: {
      error_type: "dashboard_error",
      page: context?.page || "unknown",
      component: context?.component || "unknown",
    },
    extra: {
      companyId: context?.companyId,
      page: context?.page,
      component: context?.component,
      action: context?.action,
      url: typeof window !== "undefined" ? window.location.href : undefined,
    },
    level: "error",
  });
}

/**
 * Mask email for privacy
 */
function maskEmail(email: string): string {
  if (!email || !email.includes("@")) return email;
  const [local, domain] = email.split("@");
  if (local.length <= 2) return email;
  return `${local[0]}${"*".repeat(local.length - 2)}${local[local.length - 1]}@${domain}`;
}
